import React from 'react';

import Image from '../../library/image/image';
import Typography from '../../library/typography/typography';
import Message from '../../components/message/message';
import {
  RoadRegularIcon,
  SnowflakeRegularIcon
} from '../../shared/icons/icons';
import LifespanCostWarrantyInfo from '../lifespan-cost-warranty-info/lifespan-cost-warranty-info';
import RideDataInfo from '../ride-data-info/ride-data-info';
import SnowAccelerationInfo from '../snow-acceleration-info/snow-acceleration-info';
import SnowStoppingDistanceInfo from '../snow-stopping-distance-info/snow-stopping-distance-info';
import StoppingDistanceInfo from '../stopping-distance-info/stopping-distance-info';
import TreadwellBarRow, {
  TreadwellBarRowColor
} from '../treadwell-bar-row/treadwell-bar-row';
import WeatherConditions from '../weather-conditions/weather-conditions';

import {
  useAemContent,
  useMediaQuery,
  useMyAccount
} from '../../shared/hooks/hooks';

import { ProductData, WeatherResult } from '../../shared/types/graphql-types';
import {
  AEM_CONTENT_IDS,
  AEM_CONTENT_PAGE_IDS
} from '../../shared/constants/cms';
import { TREADWELL_CONSUMER_PICKS_MSG } from '../../shared/constants/messages';

import './pdp-treadwell-section.scss';

interface PdpTreadwellSectionProps {
  product: ProductData;
  weatherDetails?: WeatherResult | null;
}

const PdpTreadwellSection = React.forwardRef<
  HTMLDivElement,
  PdpTreadwellSectionProps
>(function PdpTreadwellSection(props, ref) {
  const { product, weatherDetails } = props;
  const { isMinSm } = useMediaQuery();
  const { isFleetAccount } = useMyAccount();

  const { aemTooltipsContent } = useAemContent(
    AEM_CONTENT_PAGE_IDS.treadwellPdp
  );
  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;

  const snowPerformanceSkidControlContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.skidControl];
  const snowPerformanceStoppingDistanceContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.stoppingDistance];
  const snowPerformanceCorneringGripContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.corneringGrip];
  const snowPerformanceAccelerationContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.acceleration];
  const weatherConditionsContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.weatherConditions];
  const dryStoppingDistanceContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.dryStoppingDistance];
  const wetHandlingContent = aemTooltipsContent?.[AEM_CONTENT_IDS.wetHandling];
  const wetStoppingDistanceContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.wetStoppingDistance];
  const overallWinterRatingContent =
    aemTooltipsContent?.[AEM_CONTENT_IDS.overallWinterRating];

  const { rideRating, stopping, winter } =
    product?.recommendationData || product?.tdsData || {};
  const { dryDistanceProduct, wetDistanceProduct } =
    product?.recommendedDistanceData || {};
  const {
    brand: recommendedDryProductBrand,
    name: recommendedDryProductName,
    recommendationData: recommendedDryData,
    url: recommendedDryProductUrl
  } = dryDistanceProduct || {};
  const { stopping: recommendedDryStopping } = recommendedDryData || {};
  const {
    dryDistance: recommendedDryDistance = 0,
    dryRating: recommendedDryRating = 0
  } = recommendedDryStopping || {};
  const {
    brand: recommendedWetProductBrand,
    name: recommendedWetProductName,
    recommendationData: recommendedWetData,
    url: recommendedWetProductUrl
  } = wetDistanceProduct || {};
  const { stopping: recommendedWetStopping } = recommendedWetData || {};
  const {
    wetDistance: recommendedWetDistance = 0,
    wetRating: recommendedWetRating = 0,
    wetWornDistance: recommendedWetWornDistance = 0
  } = recommendedWetStopping || {};

  const {
    dryDistance = 0,
    dryRating,
    wetDistance = 0,
    wetRating,
    wetWornDistance = 0,
    winterRating
  } = stopping || {};
  const { comfort, handling, quiet, wetHandling } = rideRating || {};

  const showStoppingDistanceSections =
    Boolean(
      wetDistance && wetWornDistance && dryDistance && wetRating && dryRating
    ) &&
    Math.max(
      0,
      Number(dryDistance),
      Number(dryRating),
      Number(recommendedDryDistance),
      Number(recommendedDryRating),
      Number(recommendedWetDistance),
      Number(recommendedWetRating),
      Number(recommendedWetWornDistance),
      Number(wetDistance),
      Number(wetRating),
      Number(wetWornDistance)
    ) > 0;

  const showRideQualitySection = Boolean(
    comfort && handling && quiet && wetHandling
  );
  const isLT = !['SL', 'XL', 'HL'].includes(product?.loadRange || '');
  const showSnowPerformanceInfo = Number(winter?.snowBrakeRating) > 0;
  const showOverallWinterRating =
    !showSnowPerformanceInfo && showStoppingDistanceSections;

  return (
    <>
      <div
        className="pitpass-treadwell-section__logo-container"
        ref={ref}
        styleName="logo-container"
      >
        <div styleName="logo" />
        <h5>Data-tested results</h5>
      </div>

      {isFleetEnabled && (
        <Message
          data-testid="pdp-treadwell-section__treadwell-consumer-picks-msg"
          isSolidInfo
          message={TREADWELL_CONSUMER_PICKS_MSG}
          styleName="treadwell-consumer-picks-msg"
        />
      )}

      <div styleName="container">
        <h3 styleName="heading">Performance & Safety</h3>
        <WeatherConditions
          precipitationDaysPerYear={weatherDetails?.precipitationDaysPerYear}
          styleName="weather-conditions"
          title={weatherConditionsContent?.title}
          tooltip={weatherConditionsContent?.tipText}
          winterDaysPerYear={weatherDetails?.winterDaysPerYear}
        />
        <div styleName="wrapper-performance-safety">
          {showStoppingDistanceSections && (
            <>
              <div>
                <StoppingDistanceInfo
                  currentDistance={wetDistance}
                  currentRating={wetRating}
                  currentWornDistance={wetWornDistance}
                  isLT={isLT}
                  isWet
                  recommendedDistance={recommendedWetDistance}
                  recommendedProductLabel={`${recommendedWetProductBrand} ${recommendedWetProductName}`}
                  recommendedProductUrl={recommendedWetProductUrl}
                  recommendedRating={recommendedWetRating}
                  recommendedWornDistance={recommendedWetWornDistance}
                  styleName="distance-info"
                  title={wetStoppingDistanceContent?.title}
                  tooltip={wetStoppingDistanceContent?.tipText}
                />
                {Boolean(wetHandling && !isMinSm) && (
                  <TreadwellBarRow
                    barColor={TreadwellBarRowColor.BLUE}
                    icon={() => (
                      <Image
                        alt="Wet Handling Icon"
                        height="20"
                        src="https://discounttire.scene7.com/is/content/discounttire/treadwell/treadwell_ic_wet_handling.svg"
                        styleName="treadwell-bar-row-icon"
                        width="20"
                      />
                    )}
                    maxRatingLabel="high"
                    minRatingLabel="low"
                    styleName="wet-handling-in-stopping-info"
                    title={wetHandlingContent?.title || 'Handling on Wet Roads'}
                    tooltip={wetHandlingContent?.tipText}
                    value={wetHandling!}
                  />
                )}
              </div>
              <StoppingDistanceInfo
                currentDistance={dryDistance}
                currentRating={dryRating}
                isDry
                isLT={isLT}
                recommendedDistance={recommendedDryDistance}
                recommendedProductLabel={`${recommendedDryProductBrand} ${recommendedDryProductName}`}
                recommendedProductUrl={recommendedDryProductUrl}
                recommendedRating={recommendedDryRating}
                styleName="distance-info"
                title={dryStoppingDistanceContent?.title}
                tooltip={dryStoppingDistanceContent?.tipText}
              />
            </>
          )}

          <div styleName="additional-treadwell-metrics">
            {Boolean(wetHandling && isMinSm) && (
              <TreadwellBarRow
                barColor={TreadwellBarRowColor.BLUE}
                icon={() => (
                  <Image
                    alt="Wet Handling Icon"
                    height="20"
                    src="https://discounttire.scene7.com/is/content/discounttire/treadwell/treadwell_ic_wet_handling.svg"
                    styleName="treadwell-bar-row-icon"
                    width="20"
                  />
                )}
                maxRatingLabel="high"
                minRatingLabel="low"
                title={wetHandlingContent?.title || 'Handling on Wet Roads'}
                tooltip={wetHandlingContent?.tipText}
                value={wetHandling!}
              />
            )}

            {showOverallWinterRating && (
              <TreadwellBarRow
                barColor={TreadwellBarRowColor.GREY}
                icon={() => (
                  <SnowflakeRegularIcon styleName="treadwell-bar-row-icon" />
                )}
                maxRatingLabel="high"
                minRatingLabel="low"
                title={
                  overallWinterRatingContent?.title || 'Overall Winter Rating'
                }
                tooltip={overallWinterRatingContent?.tipText}
                value={winterRating || 0}
              />
            )}

            {showSnowPerformanceInfo && (
              <div styleName="snow-performance">
                <TreadwellBarRow
                  barColor={TreadwellBarRowColor.GREY}
                  icon={() => (
                    <Image
                      alt="Cornering Grip Icon"
                      height="20"
                      src="https://discounttire.scene7.com/is/content/discounttire/treadwell/treadwell_ic_cornering_grip.svg"
                      styleName="treadwell-bar-row-icon"
                      width="20"
                    />
                  )}
                  maxRatingLabel="more grip"
                  minRatingLabel="less grip"
                  title={
                    snowPerformanceCorneringGripContent?.title ||
                    'Snow Cornering Grip'
                  }
                  tooltip={snowPerformanceCorneringGripContent?.tipText}
                  value={Number(winter?.snowLateralGRating)}
                />
                <TreadwellBarRow
                  barColor={TreadwellBarRowColor.GREY}
                  icon={() => (
                    <Image
                      alt="Skid Control Icon"
                      height="20"
                      src="https://discounttire.scene7.com/is/content/discounttire/treadwell/treadwell_ic_skid_control.svg"
                      styleName="treadwell-bar-row-icon"
                      width="20"
                    />
                  )}
                  maxRatingLabel="more control"
                  minRatingLabel="less control"
                  title={
                    snowPerformanceSkidControlContent?.title ||
                    'Snow Skid Control'
                  }
                  tooltip={snowPerformanceSkidControlContent?.tipText}
                  value={Number(winter?.snowHandlingRating)}
                />
                <SnowAccelerationInfo
                  accelerationRating={Number(winter?.snowAccelerationRating)}
                  accelerationSeconds={Number(winter?.snowAccelerationTime)}
                  accelerationTitle={snowPerformanceAccelerationContent?.title}
                  accelerationTooltipText={
                    snowPerformanceAccelerationContent?.tipText
                  }
                />
                <SnowStoppingDistanceInfo
                  stoppingDistanceFeet={Number(winter?.snowBrakeFeet)}
                  stoppingDistanceRating={Number(winter?.snowBrakeRating)}
                  stoppingDistanceTitle={
                    snowPerformanceStoppingDistanceContent?.title
                  }
                  stoppingDistanceTooltipText={
                    snowPerformanceStoppingDistanceContent?.tipText
                  }
                />
              </div>
            )}
          </div>

          {showRideQualitySection && (
            <div styleName="ride-data-info">
              <div styleName="ride-data-info-title">
                <RoadRegularIcon />
                <Typography weight="bold">Ride Quality</Typography>
              </div>

              <RideDataInfo
                comfort={comfort}
                handling={handling}
                quiet={quiet}
              />
            </div>
          )}
        </div>

        <h3 styleName="heading">Lifespan, Cost & Warranty</h3>
        <LifespanCostWarrantyInfo
          product={product}
          weatherDetails={weatherDetails}
        />
      </div>
    </>
  );
});

export default PdpTreadwellSection;
